import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCookieConsent } from "context/cookie-consent-context";
import { CookieCategory } from "lib/consent/cookieConsent";
import { FiShield, FiSliders, FiX } from "react-icons/fi";

/*
 * Floating, non-blocking placement. When the signed-in mobile bottom nav is in
 * the DOM (it is display:none from lg up), both controls sit above it, clearing
 * its height plus the safe-area inset. Pure CSS — no auth/state lookups here.
 */
const FLOATING_POSITION =
  "fixed z-[9999] left-3 right-3 bottom-3 sm:left-4 sm:right-auto sm:bottom-4 " +
  "[body:has(.zs-bottomnav)_&]:bottom-[calc(var(--zs-bottomnav-h)_+_env(safe-area-inset-bottom,0px)_+_12px)] " +
  "lg:[body:has(.zs-bottomnav)_&]:bottom-4";

const SETTINGS_POSITION =
  "fixed z-[9998] left-3 bottom-3 " +
  "[body:has(.zs-bottomnav)_&]:bottom-[calc(var(--zs-bottomnav-h)_+_env(safe-area-inset-bottom,0px)_+_8px)] " +
  "lg:[body:has(.zs-bottomnav)_&]:bottom-3";

type CategoryMeta = {
  key: CookieCategory | "necessary";
  label: string;
  description: string;
  locked?: boolean;
};

/**
 * Category copy. Kept here beside the UI that shows it; the backend records its
 * own wording per category, so these two do not need to match character for
 * character — but they must describe the same thing.
 */
const CATEGORIES: CategoryMeta[] = [
  {
    key: "necessary",
    label: "Strictly necessary",
    description:
      "Required for the site to work: signing in, keeping your session, security, and carrying you through a survey. These cannot be turned off.",
    locked: true,
  },
  {
    key: "analytics",
    label: "Analytics",
    description: "Helps us understand how the site is used so we can improve it.",
  },
  {
    key: "functional",
    label: "Functional",
    description: "Remembers preferences such as your language so the site behaves as you expect.",
  },
  {
    key: "marketing",
    label: "Marketing",
    description: "Used to measure and personalise advertising on other sites.",
  },
];

/**
 * Cookie consent banner.
 *
 * Hosted by Layout, NOT by Footer. Footer is hidden on /success, /terminate,
 * /quota-full and /security-terminate — exactly the routed-participant pages —
 * and for every signed-in user, so a footer-hosted banner would be invisible to
 * most of the people who need it.
 *
 * It is non-blocking by construction: no overlay, no focus trap, no scroll lock.
 * A routed participant can complete landing -> survey -> redirect whether or not
 * they interact with it. That is a hard requirement — the survey handoff is
 * revenue-generating and latency-sensitive, and a banner that blocks it is wrong
 * regardless of how compliant it is.
 */
export const CookieBanner = () => {
  const { hasDecided, settingsOpen, openSettings, closeSettings, acceptAll, rejectAll, saveCustom } =
    useCookieConsent();

  // Every optional category starts OFF. No pre-ticking, in any locale or country.
  const [choices, setChoices] = useState<Record<CookieCategory, boolean>>({
    analytics: false,
    functional: false,
    marketing: false,
  });

  if (hasDecided && !settingsOpen) return null;

  const showCustomise = settingsOpen;

  return (
    <div role="region" aria-label="Cookie consent" className={`${FLOATING_POSITION} sm:w-[440px] animate-slideInUp`}>
      <div className="p-4 shadow-lg zs-card sm:p-5">
        {!showCustomise ? (
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <span className="zs-stat-icon" aria-hidden="true">
                <FiShield />
              </span>
              <p className="text-[13px] leading-relaxed text-ink-2">
                We use strictly necessary cookies to run this site. With your permission we would also
                use analytics, functional and marketing cookies. You can change your choice at any
                time.{" "}
                <Link to="/privacy-policy" className="zs-link">
                  Privacy policy
                </Link>
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
              <button type="button" onClick={rejectAll} className="zs-btn zs-btn-secondary zs-btn-sm">
                Reject non-essential
              </button>
              <button type="button" onClick={openSettings} className="zs-btn zs-btn-ghost zs-btn-sm">
                Customize
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="col-span-2 zs-btn zs-btn-primary zs-btn-sm"
              >
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-[15px] font-semibold font-display text-ink">Cookie preferences</h2>
              <button
                type="button"
                onClick={closeSettings}
                aria-label="Close cookie preferences"
                className="-mr-2 zs-icon-btn"
              >
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="max-h-[45vh] overflow-y-auto -mx-1 px-1">
              {CATEGORIES.map((category) => {
                const isLocked = category.locked === true;
                const checked = isLocked ? true : choices[category.key as CookieCategory];

                return (
                  <div
                    key={category.key}
                    className="flex items-start gap-3 py-3 border-b border-line last:border-b-0"
                  >
                    <input
                      id={`cookie-${category.key}`}
                      type="checkbox"
                      checked={checked}
                      disabled={isLocked}
                      onChange={(e) =>
                        setChoices((prev) => ({
                          ...prev,
                          [category.key as CookieCategory]: e.target.checked,
                        }))
                      }
                      className="mt-[3px] h-4 w-4 shrink-0 accent-primary disabled:cursor-not-allowed disabled:opacity-60"
                    />
                    <label htmlFor={`cookie-${category.key}`} className="flex-1 cursor-pointer">
                      <span className="flex flex-wrap items-center gap-2 text-[13px] font-semibold text-ink">
                        {category.label}
                        {isLocked ? <span className="zs-badge">Always on</span> : null}
                      </span>
                      <span className="mt-0.5 block text-[12px] leading-relaxed text-ink-3">
                        {category.description}
                      </span>
                    </label>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-2 pt-3 border-t border-line sm:flex sm:justify-end">
              <button type="button" onClick={rejectAll} className="zs-btn zs-btn-secondary zs-btn-sm">
                Reject non-essential
              </button>
              <button
                type="button"
                onClick={() => saveCustom(choices)}
                className="zs-btn zs-btn-primary zs-btn-sm"
              >
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * The persistent re-open control.
 *
 * Required on every page, including anonymous ones, so a decision is always
 * revisitable. Rendered by Layout for the same reason the banner is: Footer is
 * hidden on the routed-participant pages and for signed-in users.
 */
export const CookieSettingsLink = () => {
  const { hasDecided, openSettings } = useCookieConsent();

  // While the banner is up it already offers Customize, so this would duplicate.
  if (!hasDecided) return null;

  return (
    <button
      type="button"
      onClick={openSettings}
      className={`${SETTINGS_POSITION} inline-flex items-center gap-1.5 rounded-full border border-line bg-surface/95 px-3 py-1.5 text-[11px] font-medium text-ink-3 shadow-sm backdrop-blur hover:bg-surface hover:text-ink`}
    >
      <FiSliders aria-hidden="true" className="w-3 h-3" />
      Cookie settings
    </button>
  );
};
